// src/pages/ApplicationChampionshipSeason/shared/steps/Step4Review/Step4SeasonReview.tsx
import { FunctionComponent } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SeasonType } from '@/model';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { useChampionships } from '@/services/queries/championship';

import { SeasonFormData } from '../../schemas';
import { buildStagesSummary } from '../../utils/buildStagesSummary';
import { WizardStepFooter } from '../../components/WizardStepFooter';
import { InfoCallout } from '../../components/InfoCallout';
import { ReviewSummaryGrid } from './ReviewSummaryGrid';
import { ReviewStagesChips } from './ReviewStagesChips';

type Step4SeasonReviewProps = {
  onBack: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
  /**
   * Sobrescreve o label do botão principal. Usado no fluxo de Edit quando a
   * temporada já tem stages — botão vira "Concluir" em vez de "Criar temporada".
   */
  primaryLabel?: string;
};

const PREFIX = 'pages.application_season.components.season_step3';

const FORMAT_KEY: Record<SeasonType, string> = {
  [SeasonType.LEAGUE]: 'league',
  [SeasonType.KNOCKOUT]: 'cup',
  [SeasonType.HYBRID]: 'hybrid',
};

export const Step4SeasonReview: FunctionComponent<Step4SeasonReviewProps> = ({
  onBack,
  onCancel,
  onConfirm,
  isSubmitting,
  primaryLabel,
}) => {
  const { t } = useTranslation();
  const { watch } = useFormContext<SeasonFormData>();
  const values = watch();

  const { data } = useChampionships();
  const championshipName = (data?.data ?? []).find(item => item.uuid === values.championship_uuid)?.name ?? '';

  const formatLabel = values.season_type
    ? t(`pages.application_season.components.season_step1.fields.format.${FORMAT_KEY[values.season_type]}.label`)
    : '';

  const stages = buildStagesSummary(values);

  return (
    <Flex direction='column' gap={6}>
      <Text fontSize='18px' fontWeight='semibold'>
        {t(`${PREFIX}.title`)}
      </Text>

      <InfoCallout>{t(`${PREFIX}.callout`)}</InfoCallout>

      <ReviewSummaryGrid
        championshipName={championshipName}
        year={values.season_year ?? ''}
        formatLabel={formatLabel}
        totalStages={stages.length}
        totalTeams={values.season_teams?.length ?? 0}
      />

      <ReviewStagesChips stages={stages} />

      <WizardStepFooter
        onBack={onBack}
        onCancel={onCancel}
        primaryLabel={primaryLabel ?? t('pages.application_season.components.season_wizard.footer.create')}
        onPrimary={onConfirm}
        primaryLoading={isSubmitting}
        primaryColorScheme='green'
        primaryLeftIcon={<ChakraFeatherIcon name='check' boxSize='16px' />}
      />
    </Flex>
  );
};
